import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, MessageSquare, CheckCircle, Bell, Shield,
  LogOut, TrendingUp, Send, ChevronRight, Eye, Target,
  X, Calendar, CalendarPlus, BookOpen, Heart, Briefcase, HelpCircle, Reply, FileText, Megaphone
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ResourceManagement from '@/components/ResourceManagement';
import NotificationBell from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const YAS_LOGO = '/yas-logo.png';

// Parent Messages Tab Component
const ParentMessagesTab = ({ parentMessages, formatDate, onRefresh }) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  // Group messages by parent for conversation view
  const groupedByParent = parentMessages.reduce((acc, msg) => {
    // Determine the parent ID (either sender or receiver depending on message direction)
    const parentId = msg.from_role === 'parent' ? msg.from_id : msg.to_id;
    const parentName = msg.from_role === 'parent' ? msg.from_name : null;
    const youthName = msg.youth_name || msg.child_name;
    
    if (!acc[parentId]) {
      acc[parentId] = {
        parentId,
        parentName: parentName || 'Parent',
        youthName: youthName || 'Youth',
        messages: []
      };
    }
    acc[parentId].messages.push(msg);
    // Update parent name if we find it
    if (parentName) {
      acc[parentId].parentName = parentName;
    }
    if (youthName) {
      acc[parentId].youthName = youthName;
    }
    return acc;
  }, {});

  // Sort messages within each conversation by date
  Object.values(groupedByParent).forEach(convo => {
    convo.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const sendReply = async () => {
    if (!replyText.trim() || !replyingTo) {
      toast.error('Please write a reply');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/lead-monitor/reply-parent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content: replyText,
          parent_id: replyingTo.parentId
        })
      });

      if (res.ok) {
        toast.success('Reply sent!');
        setReplyText('');
        setReplyingTo(null);
        onRefresh();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send reply');
      }
    } catch (error) {
      toast.error('Connection error');
    }
    setSending(false);
  };

  const conversations = Object.values(groupedByParent);

  return (
    <Card className="p-6 rounded-3xl">
      <h2 className="font-heading text-xl font-bold mb-6">Parent Messages</h2>

      {conversations.length === 0 ? (
        <div className="text-center py-12">
          <Heart className="w-16 h-16 text-purple-500 mx-auto mb-4" />
          <p className="text-xl font-semibold text-purple-600">No parent messages yet</p>
          <p className="text-muted-foreground">Parents haven't sent any messages</p>
        </div>
      ) : (
        <div className="space-y-6">
          {conversations.map((convo) => (
            <div key={convo.parentId} className="border rounded-2xl overflow-hidden">
              {/* Conversation Header */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-200 to-pink-200 rounded-xl flex items-center justify-center text-xl">
                      👨‍👩‍👧‍👦
                    </div>
                    <div>
                      <span className="font-semibold">{convo.parentName}</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        Parent of {convo.youthName}
                      </Badge>
                    </div>
                  </div>
                  <Badge className="bg-purple-100 text-purple-800">
                    {convo.messages.length} message{convo.messages.length !== 1 ? 's' : ''}
                  </Badge>
                </div>
              </div>

              {/* Messages Thread */}
              <div className="p-4 space-y-3 max-h-64 overflow-y-auto bg-gray-50">
                {convo.messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`flex ${msg.from_role === 'parent' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div 
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.from_role === 'parent' 
                          ? 'bg-white border border-gray-200 rounded-tl-sm' 
                          : 'bg-purple-500 text-white rounded-tr-sm'
                      }`}
                    >
                      <p className={`text-sm ${msg.from_role === 'parent' ? 'text-gray-800' : 'text-white'}`}>
                        {msg.content}
                      </p>
                      <p className={`text-xs mt-1 ${msg.from_role === 'parent' ? 'text-gray-400' : 'text-purple-200'}`}>
                        {formatDate(msg.created_at)}
                        {msg.from_role !== 'parent' && ' • You'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="p-4 bg-white border-t">
                {replyingTo?.parentId === convo.parentId ? (
                  <div className="space-y-3">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Reply to ${convo.parentName}...`}
                      className="rounded-xl min-h-[80px]"
                      data-testid={`reply-input-${convo.parentId}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => { setReplyingTo(null); setReplyText(''); }}
                        className="rounded-full"
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={sendReply}
                        disabled={sending}
                        className="rounded-full bg-purple-500 hover:bg-purple-600"
                        data-testid={`send-reply-${convo.parentId}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {sending ? 'Sending...' : 'Send Reply'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    variant="outline" 
                    onClick={() => setReplyingTo(convo)}
                    className="w-full rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"
                    data-testid={`reply-btn-${convo.parentId}`}
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply to {convo.parentName}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

const LeadMonitorDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [assignedYouth, setAssignedYouth] = useState([]);
  const [groupMessages, setGroupMessages] = useState([]);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [selectedYouth, setSelectedYouth] = useState(null);
  const [youthGoals, setYouthGoals] = useState([]);
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [allHighlights, setAllHighlights] = useState([]);
  const [selectedYouthJournal, setSelectedYouthJournal] = useState(null);
  const [youthHighlights, setYouthHighlights] = useState([]);
  const [parentMessages, setParentMessages] = useState([]);
  
  // NEW: Consent tracking
  const [consentData, setConsentData] = useState(null);
  const [consentFilter, setConsentFilter] = useState('all');
  const [resendingConsent, setResendingConsent] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [youthTimeline, setYouthTimeline] = useState(null);
  const [timelineYouthId, setTimelineYouthId] = useState(null);
  const [sendMsgOpen, setSendMsgOpen] = useState(false);
  const [msgContent, setMsgContent] = useState('');
  const [msgMustRead, setMsgMustRead] = useState(false);
  const [msgSending, setMsgSending] = useState(false);
  const [aiReport, setAiReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [helpdeskQuestions, setHelpdeskQuestions] = useState([]);

  useEffect(() => {
    fetchAllData();
    fetchConsentStatusData();
    fetchAIInsights();
    fetchActivityFeed();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAIInsights = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai/staff-insights`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setAiInsights(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchActivityFeed = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/staff/activity-feed`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setActivityFeed(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchYouthTimeline = async (youthId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/staff/youth-timeline/${youthId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { setYouthTimeline(await res.json()); setTimelineYouthId(youthId); }
    } catch (e) { console.error(e); }
  };

  const sendStaffMessage = async () => {
    if (!msgContent.trim()) { toast.error('Write a message'); return; }
    setMsgSending(true);
    try {
      const token = localStorage.getItem('token');
      const recipients = assignedYouth.map(y => y.id);
      const res = await fetch(`${API_URL}/api/staff/send-message`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: msgContent, must_read: msgMustRead, priority: msgMustRead ? 'urgent' : 'normal', recipient_ids: recipients })
      });
      if (res.ok) { toast.success(`Message sent to ${recipients.length} youth`); setSendMsgOpen(false); setMsgContent(''); setMsgMustRead(false); }
    } catch (e) { toast.error('Failed to send'); }
    setMsgSending(false);
  };

  const generateAIReport = async (youthId) => {
    setReportLoading(true);
    setAiReport(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/staff/youth-report/${youthId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        setAiReport(await res.json());
        toast.success('Report generated');
      } else { toast.error('Failed to generate report'); }
    } catch (e) { toast.error('Report generation failed'); }
    setReportLoading(false);
  };
  
  const fetchConsentStatusData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/staff/consent-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setConsentData(await res.json());
      }
    } catch (error) {
      console.error('Error fetching consent status:', error);
    }
  };

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [youthRes, messagesRes, moderationRes, analyticsRes, highlightsRes, parentMsgRes] = await Promise.all([
        fetch(`${API_URL}/api/lead-monitor/my-youth`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/messages/group`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/moderation/queue`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/coach/analytics`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/highlights/group`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/parent/messages`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (youthRes.ok) setAssignedYouth(await youthRes.json());
      if (messagesRes.ok) setGroupMessages(await messagesRes.json());
      if (moderationRes.ok) setModerationQueue(await moderationRes.json());
      if (analyticsRes.ok) setAnalytics(await analyticsRes.json());
      if (highlightsRes.ok) setAllHighlights(await highlightsRes.json());
      if (parentMsgRes.ok) setParentMessages(await parentMsgRes.json());
      
      // Fetch helpdesk questions
      try {
        const qRes = await fetch(`${API_URL}/api/helpdesk/questions?filter=all`, { headers: { Authorization: `Bearer ${token}` } });
        if (qRes.ok) setHelpdeskQuestions(await qRes.json());
      } catch (e) { /* ignore */ }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };
  
  const resendConsentEmail = async (youthId, youthName) => {
    setResendingConsent(youthId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/staff/resend-consent/${youthId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Consent reminder sent for ${youthName}!`);
        fetchConsentStatusData();
      } else {
        toast.error('Failed to send reminder');
      }
    } catch (error) {
      toast.error('Error sending reminder');
    } finally {
      setResendingConsent(null);
    }
  };
  
  const getConsentStatusForYouth = (youthId) => {
    if (!consentData) return null;
    return consentData.youth_details?.find(y => y.id === youthId);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const sendGroupMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please write a message');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/messages/group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content: newMessage, is_mandatory: false })
      });

      if (res.ok) {
        toast.success('Message sent to your group!');
        setShowMessageModal(false);
        setNewMessage('');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const moderatePost = async (postId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/moderation/${postId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ action })
      });

      if (res.ok) {
        toast.success(`Post ${action}d`);
        fetchAllData();
      }
    } catch (error) {
      toast.error('Moderation failed');
    }
  };

  const viewYouthGoals = async (youth) => {
    setSelectedYouth(youth);
    setLoadingGoals(true);
    try {
      const res = await fetch(`${API_URL}/api/lead-monitor/youth/${youth.id}/goals`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setYouthGoals(data.goals || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load goals');
    }
    setLoadingGoals(false);
  };

  const addGoalToCalendar = (goal, youthName) => {
    const startDate = new Date(goal.deadline);
    const endDate = new Date(goal.deadline);
    endDate.setHours(endDate.getHours() + 1);
    
    const formatDate = (date) => date.toISOString().replace(/-|:|\.\d\d\d/g, '');
    
    const url = new URL('https://calendar.google.com/calendar/render');
    url.searchParams.set('action', 'TEMPLATE');
    url.searchParams.set('text', `🎯 ${youthName}'s Goal: ${goal.title}`);
    url.searchParams.set('details', `SYEP Goal Deadline\n\n${goal.specific || ''}\n\nMonitoring via YAS Connect`);
    url.searchParams.set('dates', `${formatDate(startDate)}/${formatDate(endDate)}`);
    
    window.open(url.toString(), '_blank');
    toast.success('Opening Google Calendar...');
  };

  const fetchAllHighlights = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/highlights/group`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setAllHighlights(await res.json());
      }
    } catch (error) {
      console.error('Error fetching highlights:', error);
    }
  };

  const viewYouthJournal = async (youth) => {
    setSelectedYouthJournal(youth);
    try {
      const res = await fetch(`${API_URL}/api/highlights/youth/${youth.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        setYouthHighlights(await res.json());
      }
    } catch (error) {
      console.error('Error fetching youth journal:', error);
      toast.error('Failed to load journal');
    }
  };

  const getCategoryInfo = (categoryId) => {
    const categories = {
      experience: { label: 'Work Experience', icon: '💼', color: 'bg-blue-500' },
      learned: { label: 'Something Learned', icon: '💡', color: 'bg-yellow-500' },
      challenge: { label: 'Challenge Faced', icon: '💪', color: 'bg-orange-500' },
      achievement: { label: 'Achievement', icon: '🏆', color: 'bg-green-500' },
      grateful: { label: 'Grateful For', icon: '🙏', color: 'bg-purple-500' }
    };
    return categories[categoryId] || categories.experience;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  useEffect(() => {
    fetchAllHighlights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-2xl font-heading font-bold text-primary">Loading...</div>
      </div>
    );
  }

  const completedCount = assignedYouth.filter(y => y.checkin_complete).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="p-6 text-white" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-start mb-6">

          {/* Consent Status Filter */}
          {consentData && (
            <div className="flex gap-2 mb-6">
              <Button
                variant={consentFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setConsentFilter('all')}
                size="sm"
              >
                All Youth ({consentData.total_youth})
              </Button>
              <Button
                variant={consentFilter === 'consented' ? 'default' : 'outline'}
                onClick={() => setConsentFilter('consented')}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                ✅ Consented ({consentData.consented})
              </Button>
              <Button
                variant={consentFilter === 'pending' ? 'default' : 'outline'}
                onClick={() => setConsentFilter('pending')}
                size="sm"
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                🟡 Pending Consent ({consentData.pending})
              </Button>
            </div>
          )}

            <div className="flex items-center gap-4">
              <img src={YAS_LOGO} alt="SYEP" className="w-20 h-20 object-contain bg-white rounded-xl p-2 shadow-md" />
              <div>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>Staff Dashboard</p>
                <h1 className="font-heading text-2xl font-bold" style={{ color: "white" }}>{user.name}</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <Button onClick={handleLogout} variant="ghost" className="text-white hover:bg-white/20 rounded-full">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-white/10 backdrop-blur border-white/20 p-4 rounded-2xl">
              <Users className="w-5 h-5 mb-1 opacity-75" />
              <p className="text-2xl font-bold">{assignedYouth.length}</p>
              <p className="text-xs opacity-75">My Youth</p>
            </Card>
            <Card className="bg-white/10 backdrop-blur border-white/20 p-4 rounded-2xl">
              <CheckCircle className="w-5 h-5 mb-1 opacity-75" />
              <p className="text-2xl font-bold">{completedCount}/{assignedYouth.length}</p>
              <p className="text-xs opacity-75">Check-ins</p>
            </Card>
            <Card className="bg-white/10 backdrop-blur border-white/20 p-4 rounded-2xl">
              <BookOpen className="w-5 h-5 mb-1 opacity-75" />
              <p className="text-2xl font-bold">{assignedYouth.reduce((s, y) => s + (y.courses?.completed || 0), 0)}</p>
              <p className="text-xs opacity-75">Courses Done</p>
            </Card>
            <Card className={`bg-white/10 backdrop-blur border-white/20 p-4 rounded-2xl ${assignedYouth.filter(y => y.risk_level === 'high' || y.risk_level === 'medium').length > 0 ? 'ring-1 ring-red-400/50' : ''}`}>
              <Bell className="w-5 h-5 mb-1 opacity-75" />
              <p className="text-2xl font-bold">{assignedYouth.filter(y => y.risk_level === 'high' || y.risk_level === 'medium').length}</p>
              <p className="text-xs opacity-75">Needs Attention</p>
            </Card>
            <Card className="bg-white/10 backdrop-blur border-white/20 p-4 rounded-2xl">
              <Target className="w-5 h-5 mb-1 opacity-75" />
              <p className="text-2xl font-bold">{assignedYouth.reduce((s, y) => s + (y.goals_summary?.completed || 0), 0)}</p>
              <p className="text-xs opacity-75">Goals Done</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">

        {/* Activity Feed */}
        {activityFeed.length > 0 && (
          <Card className="p-4 rounded-2xl mb-5 border border-gray-100" data-testid="activity-feed">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Bell className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: colors.text }}>Recent Activity</p>
                  <p className="text-[10px] text-gray-400">High-signal updates from your youth</p>
                </div>
              </div>
              <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={() => setSendMsgOpen(true)} data-testid="send-msg-btn">
                <Send className="w-3 h-3 mr-1" /> Message All
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {activityFeed.slice(0, 8).map((evt, i) => (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-xl text-sm ${evt.priority === 'high' ? 'bg-amber-50' : 'bg-gray-50'}`}>
                  <span className="text-base">{evt.type === 'course_completed' ? '📚' : evt.type === 'goal_completed' ? '🎯' : evt.type === 'win_posted' ? '🏆' : evt.type === 'badge_earned' ? '⭐' : '📌'}</span>
                  <span className="flex-1"><strong>{evt.user_name}</strong> {evt.type === 'course_completed' ? 'completed a course' : evt.type === 'goal_completed' ? 'completed a goal' : evt.type === 'win_posted' ? 'posted a win' : evt.type === 'badge_earned' ? `earned ${evt.detail}` : evt.detail}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Send Message Dialog */}
        <Dialog open={sendMsgOpen} onOpenChange={setSendMsgOpen}>
          <DialogContent className="rounded-2xl max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">Send Message to Youth</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="p-3 rounded-xl bg-gray-50 text-sm text-gray-600">
                Sending to: <strong>All My Youth ({assignedYouth.length})</strong>
              </div>
              <Textarea value={msgContent} onChange={e => setMsgContent(e.target.value)} placeholder="Write your message..." rows={4}
                className="rounded-xl" data-testid="msg-content" />
              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50" data-testid="must-read-toggle">
                <input type="checkbox" checked={msgMustRead} onChange={e => setMsgMustRead(e.target.checked)} className="w-4 h-4 accent-orange-500" />
                <div>
                  <p className="text-sm font-bold text-gray-900">Must Read</p>
                  <p className="text-[10px] text-gray-400">Youth must acknowledge before using the platform</p>
                </div>
              </label>
              <Button onClick={sendStaffMessage} disabled={msgSending || !msgContent.trim()}
                className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="send-msg-confirm">
                {msgSending ? 'Sending...' : `Send to ${assignedYouth.length} Youth`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Youth Timeline Dialog */}
        <Dialog open={!!youthTimeline} onOpenChange={() => { setYouthTimeline(null); setTimelineYouthId(null); setAiReport(null); }}>
          <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
            {youthTimeline && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <span className="text-2xl">{youthTimeline.youth.avatar_emoji || '😊'}</span>
                    <div>
                      <p className="text-lg font-bold">{youthTimeline.youth.name}</p>
                      <p className="text-xs text-gray-400">{youthTimeline.youth.points} pts · {youthTimeline.youth.streak} day streak</p>
                    </div>
                  </DialogTitle>
                </DialogHeader>
                {/* Workshop summary */}
                {youthTimeline.course_summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <div className="p-2 rounded-xl bg-emerald-50 text-center"><p className="text-lg font-bold text-emerald-700">{youthTimeline.course_summary?.completed || 0}</p><p className="text-[9px] text-emerald-600">Done</p></div>
                  <div className="p-2 rounded-xl bg-orange-50 text-center"><p className="text-lg font-bold text-orange-700">{youthTimeline.course_summary?.in_progress || 0}</p><p className="text-[9px] text-orange-600">In Progress</p></div>
                  <div className="p-2 rounded-xl bg-amber-50 text-center"><p className="text-lg font-bold text-amber-700">{youthTimeline.course_summary?.total_xp || 0}</p><p className="text-[9px] text-amber-600">XP</p></div>
                  <div className="p-2 rounded-xl bg-violet-50 text-center"><p className="text-lg font-bold text-violet-700">{youthTimeline.course_summary?.badges_earned || 0}</p><p className="text-[9px] text-violet-600">Badges</p></div>
                </div>
                )}
                {/* Timeline */}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="rounded-xl text-xs flex-1" 
                    onClick={() => generateAIReport(youthTimeline.youth.id)} disabled={reportLoading}
                    data-testid="generate-report-btn">
                    {reportLoading ? 'Generating...' : 'Generate AI Profile Report'}
                  </Button>
                </div>

                {/* AI Report */}
                {aiReport && (
                  <Card className="p-4 rounded-2xl mt-3 bg-gradient-to-br from-violet-50 to-orange-50 border-violet-200">
                    <p className="text-xs font-bold text-violet-800 mb-2">AI Profile Report — {aiReport.youth_name}</p>
                    {aiReport.report.summary && <p className="text-sm text-gray-700 mb-2 italic">{aiReport.report.summary}</p>}
                    {aiReport.report.strengths?.length > 0 && (
                      <div className="mb-2"><p className="text-[10px] font-bold text-emerald-700 uppercase mb-1">Strengths</p>
                        {aiReport.report.strengths.map((s, i) => <p key={i} className="text-xs text-gray-600 ml-2">+ {s}</p>)}
                      </div>
                    )}
                    {aiReport.report.growth_areas?.length > 0 && (
                      <div className="mb-2"><p className="text-[10px] font-bold text-amber-700 uppercase mb-1">Growth Areas</p>
                        {aiReport.report.growth_areas.map((g, i) => <p key={i} className="text-xs text-gray-600 ml-2">~ {g}</p>)}
                      </div>
                    )}
                    {aiReport.report.recommendations?.length > 0 && (
                      <div className="mb-2"><p className="text-[10px] font-bold text-blue-700 uppercase mb-1">Recommendations</p>
                        {aiReport.report.recommendations.map((r, i) => <p key={i} className="text-xs text-gray-600 ml-2">→ {r}</p>)}
                      </div>
                    )}
                    {aiReport.report.full_narrative && (
                      <details className="mt-2"><summary className="text-[10px] text-violet-600 cursor-pointer font-bold">Full Narrative Report</summary>
                        <p className="text-xs text-gray-600 mt-2 whitespace-pre-wrap leading-relaxed">{aiReport.report.full_narrative}</p>
                      </details>
                    )}
                  </Card>
                )}

                <div className="mt-4 space-y-0 relative">
                  <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200" />
                  {youthTimeline.timeline.map((evt, i) => (
                    <div key={i} className="flex items-start gap-3 relative pl-9 py-2">
                      <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-white ${evt.type.includes('done') || evt.type.includes('completed') ? 'bg-emerald-500' : evt.type === 'joined' ? 'bg-gray-400' : evt.type.includes('stalled') ? 'bg-red-400' : 'bg-orange-400'}`} />
                      <div className="flex-1">
                        <p className="text-sm text-gray-800">{evt.title}</p>
                        {evt.date && <p className="text-[10px] text-gray-400">{new Date(evt.date).toLocaleDateString()}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Tabs defaultValue="youth">
          <TabsList className="bg-white rounded-2xl p-1 shadow-sm mb-6 flex-wrap">
            <TabsTrigger value="youth" className="rounded-xl">
              <Users className="w-4 h-4 mr-2" /> My Youth ({assignedYouth.length})
            </TabsTrigger>
            <TabsTrigger value="workshops" className="rounded-xl">
              <BookOpen className="w-4 h-4 mr-2" /> Courses
            </TabsTrigger>
            <TabsTrigger value="messages" className="rounded-xl">
              <MessageSquare className="w-4 h-4 mr-2" /> Messages
              {(parentMessages.length + groupMessages.length) > 0 && (
                <Badge className="ml-2 bg-blue-500">{parentMessages.length + groupMessages.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="journal" className="rounded-xl">
              <FileText className="w-4 h-4 mr-2" /> Journals
            </TabsTrigger>
            <TabsTrigger value="moderation" className="rounded-xl">
              <Shield className="w-4 h-4 mr-2" /> Moderation
              {moderationQueue.length > 0 && (
                <Badge className="ml-2 bg-red-500">{moderationQueue.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* AI Insights Panel — Above My Youth Tab */}
          {aiInsights && (aiInsights.alerts?.length > 0 || aiInsights.celebrations?.length > 0) && (
            <Card className="p-4 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #FEF3C7 100%)', border: '1px solid #DDD6FE' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-violet-900">AI Insights</p>
                  <p className="text-xs text-violet-700">{aiInsights.summary}</p>
                </div>
              </div>
              <div className="space-y-2">
                {aiInsights.alerts?.slice(0, 3).map((a, i) => (
                  <div key={`a${i}`} className="flex items-start gap-2 p-2.5 rounded-xl bg-red-50 border border-red-100">
                    <span className="text-sm flex-shrink-0">{a.avatar || '🎓'}</span>
                    <p className="text-xs text-red-800">{a.message}</p>
                  </div>
                ))}
                {aiInsights.celebrations?.slice(0, 3).map((c, i) => (
                  <div key={`c${i}`} className="flex items-start gap-2 p-2.5 rounded-xl bg-green-50 border border-green-100">
                    <span className="text-sm flex-shrink-0">{c.avatar || '🎓'}</span>
                    <p className="text-xs text-green-800">{c.message}</p>
                  </div>
                ))}
                {aiInsights.recommendations?.slice(0, 2).map((r, i) => (
                  <div key={`r${i}`} className="flex items-start gap-2 p-2.5 rounded-xl bg-blue-50 border border-blue-100">
                    <span className="text-sm flex-shrink-0">{r.avatar || '🎓'}</span>
                    <p className="text-xs text-blue-800">{r.message}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* My Youth Tab */}
          <TabsContent value="youth">
            {/* Risk filter + Nudge bar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {['all', 'at_risk', 'no_courses', 'stalled'].map(f => {
                const count = f === 'all' ? assignedYouth.length
                  : f === 'at_risk' ? assignedYouth.filter(y => y.risk_level === 'high' || y.risk_level === 'medium').length
                  : f === 'no_courses' ? assignedYouth.filter(y => !y.courses || (y.courses.completed === 0 && y.courses.in_progress === 0)).length
                  : assignedYouth.filter(y => y.courses?.in_progress > 0 && y.courses?.completed === 0).length;
                const label = f === 'all' ? 'All' : f === 'at_risk' ? 'Needs Attention' : f === 'no_courses' ? 'No Courses' : 'Stalled';
                return (
                  <button key={f} onClick={() => setConsentFilter(f)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${consentFilter === f ? 'bg-[#0A0E1A] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
                    data-testid={`filter-${f}`}>
                    {label} ({count})
                  </button>
                );
              })}
              {consentFilter !== 'all' && (
                <Button size="sm" variant="outline" className="rounded-xl text-xs ml-auto" data-testid="nudge-btn"
                  onClick={() => {
                    const filtered = assignedYouth.filter(y => {
                      if (consentFilter === 'at_risk') return y.risk_level === 'high' || y.risk_level === 'medium';
                      if (consentFilter === 'no_courses') return !y.courses || (y.courses.completed === 0 && y.courses.in_progress === 0);
                      if (consentFilter === 'stalled') return y.courses?.in_progress > 0 && y.courses?.completed === 0;
                      return false;
                    });
                    if (filtered.length > 0) { setSendMsgOpen(true); }
                  }}>
                  <Send className="w-3 h-3 mr-1" /> Nudge This Group
                </Button>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {assignedYouth.length === 0 ? (
                <Card className="p-8 text-center rounded-3xl col-span-3">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No youth assigned yet</p>
                </Card>
              ) : (
                assignedYouth
                  .filter(youth => {
                    if (consentFilter === 'all') return true;
                    if (consentFilter === 'at_risk') return youth.risk_level === 'high' || youth.risk_level === 'medium';
                    if (consentFilter === 'no_courses') return !youth.courses || (youth.courses.completed === 0 && youth.courses.in_progress === 0);
                    if (consentFilter === 'stalled') return youth.courses?.in_progress > 0 && youth.courses?.completed === 0;
                    // Legacy consent filters
                    const status = getConsentStatusForYouth(youth.id);
                    if (!status) return consentFilter === 'all';
                    return status.consent_status === consentFilter;
                  })
                  .map((youth) => (
                  <motion.div key={youth.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                    <Card 
                      className={`rounded-2xl hover:shadow-lg transition-all cursor-pointer overflow-hidden ${
                        youth.risk_level === 'high' ? 'ring-2 ring-red-300' : youth.risk_level === 'medium' ? 'ring-1 ring-amber-300' : ''
                      }`}
                      data-testid={`youth-card-${youth.id}`}
                      onClick={() => fetchYouthTimeline(youth.id)}
                    >
                      {/* Risk flag bar */}
                      {youth.risk_level === 'high' && <div className="h-1 bg-red-500" />}
                      {youth.risk_level === 'medium' && <div className="h-1 bg-amber-400" />}
                      
                      <div className="p-4">
                        {/* Header: Avatar + Name + Risk badge */}
                        <div className="flex items-start gap-3 mb-3">
                          <Avatar className="w-12 h-12 ring-2 ring-purple-100 flex-shrink-0">
                            {youth.profile_picture ? <AvatarImage src={youth.profile_picture} alt={youth.name} /> : null}
                            <AvatarFallback className="bg-gradient-to-br from-purple-100 to-indigo-100 text-xl">
                              {youth.avatar_emoji || '😊'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-bold text-sm truncate">{youth.name}</h3>
                              {youth.risk_level === 'high' && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700">AT RISK</span>}
                            </div>
                            {youth.birthday && <p className="text-[10px] text-gray-400">{new Date(youth.birthday).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>}
                            {(youth.work_site || youth.school) && (
                              <p className="text-[10px] text-gray-500 truncate">{youth.work_site || youth.school}</p>
                            )}
                          </div>
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8 flex-shrink-0"
                            onClick={(e) => { e.stopPropagation(); fetchYouthTimeline(youth.id); }}
                            data-testid={`view-details-${youth.id}`}>
                            <Eye className="w-4 h-4 text-gray-400" />
                          </Button>
                        </div>

                        {/* Interests */}
                        {youth.profile_interests?.length > 0 && (
                          <div className="flex gap-1 flex-wrap mb-2">
                            {youth.profile_interests.slice(0, 4).map((int, i) => (
                              <span key={i} className="px-2 py-0.5 rounded-full text-[9px] font-medium bg-blue-50 text-blue-600 capitalize">{int.replace(/_/g, ' ')}</span>
                            ))}
                          </div>
                        )}

                        {/* Career / Future interests */}
                        {(youth.career_interests?.length > 0 || youth.post_secondary?.length > 0 || youth.summer_goals?.length > 0) && (
                          <p className="text-[10px] text-gray-500 mb-2 truncate">
                            {youth.post_secondary?.length > 0
                              ? `${youth.post_secondary[0].school_name || youth.post_secondary[0].school_type || 'Post-secondary'}`
                              : youth.career_interests?.length > 0
                              ? `Career: ${youth.career_interests.slice(0, 2).join(', ')}`
                              : youth.summer_goals?.length > 0
                              ? `Goal: ${youth.summer_goals[0]}`
                              : ''}
                          </p>
                        )}

                        {/* Quick stats row */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-bold text-amber-600">{youth.total_points || 0} pts</span>
                          {youth.courses && (youth.courses.completed > 0 || youth.courses.in_progress > 0) ? (
                            <span className="text-[10px] font-medium text-violet-600">{youth.courses.completed} done · {youth.courses.in_progress} in progress</span>
                          ) : (
                            <span className="text-[10px] text-gray-400">No courses</span>
                          )}
                          {youth.goals_summary && (
                            <span className="text-[10px] text-blue-500">{youth.goals_summary.completed} goals done</span>
                          )}
                        </div>

                        {/* Risk flags */}
                        {youth.risk_flags?.length > 0 && (
                          <div className="flex gap-1 flex-wrap mt-2">
                            {youth.risk_flags.slice(0, 2).map((flag, i) => (
                              <span key={i} className={`px-2 py-0.5 rounded-full text-[9px] font-medium ${flag.severity === 'high' ? 'bg-red-100 text-red-700' : flag.severity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                {flag.detail}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  </motion.div>
                ))
              )}
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="workshops">
            <div className="space-y-4">
              {/* Workshop KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card className="p-4 rounded-2xl bg-emerald-50 border-emerald-100">
                  <p className="text-2xl font-black text-emerald-700">{analytics?.courses?.total_completions || 0}</p>
                  <p className="text-xs text-emerald-600 font-medium">Total Completions</p>
                </Card>
                <Card className="p-4 rounded-2xl bg-orange-50 border-orange-100">
                  <p className="text-2xl font-black text-orange-700">{analytics?.courses?.total_started || 0}</p>
                  <p className="text-xs text-orange-600 font-medium">Courses Started</p>
                </Card>
                <Card className="p-4 rounded-2xl bg-blue-50 border-blue-100">
                  <p className="text-2xl font-black text-blue-700">{analytics?.courses?.youth_engaged || 0}</p>
                  <p className="text-xs text-blue-600 font-medium">Youth Engaged</p>
                </Card>
                <Card className="p-4 rounded-2xl bg-red-50 border-red-100">
                  <p className="text-2xl font-black text-red-700">{analytics?.courses?.stalled || 0}</p>
                  <p className="text-xs text-red-600 font-medium">Stalled</p>
                </Card>
              </div>

              {/* Per-course breakdown */}
              <Card className="p-4 rounded-2xl">
                <h3 className="font-bold text-sm mb-3">Course Breakdown</h3>
                <div className="space-y-2">
                  {Object.entries(analytics?.courses?.by_course || {}).map(([cid, stats]) => {
                    const title = cid.replace('yc_course_', '').replace('_v1', '').replace(/_/g, ' ');
                    const pct = stats.started > 0 ? Math.round((stats.completed / stats.started) * 100) : 0;
                    return (
                      <div key={cid} className="flex items-center gap-3 p-2 rounded-xl bg-gray-50">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium capitalize truncate">{title}</p>
                          <div className="h-1.5 bg-gray-200 rounded-full mt-1">
                            <div className="h-1.5 bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs font-bold text-gray-700">{stats.completed}/{stats.started}</p>
                          <p className="text-[9px] text-gray-400">{pct}% complete</p>
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(analytics?.courses?.by_course || {}).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No course data yet</p>
                  )}
                </div>
              </Card>

              {/* Youth course breakdown */}
              <Card className="p-4 rounded-2xl">
                <h3 className="font-bold text-sm mb-3">Per-Youth Workshop Status</h3>
                <div className="space-y-2">
                  {assignedYouth.map(y => (
                    <div key={y.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 cursor-pointer" onClick={() => fetchYouthTimeline(y.id)}>
                      <span className="text-lg">{y.avatar_emoji || '😊'}</span>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{y.name}</p>
                      </div>
                      <div className="flex gap-2 text-[10px]">
                        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">{y.courses?.completed || 0} done</span>
                        <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">{y.courses?.in_progress || 0} active</span>
                        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-bold">{13 - (y.courses?.completed || 0) - (y.courses?.in_progress || 0)} not started</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Messages Tab — Combined Group + Parent */}
          <TabsContent value="messages">
            <div className="space-y-4">
              {/* Youth Q&A Questions — Answer + Promote to FAQ */}
              {helpdeskQuestions.length > 0 && (
                <Card className="p-4 rounded-2xl">
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-orange-500" /> Youth Questions ({helpdeskQuestions.filter(q => q.status !== 'answered').length} unanswered)</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {helpdeskQuestions.slice(0, 10).map(q => (
                      <div key={q.id} className={`p-3 rounded-xl ${q.status === 'answered' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                        <p className="text-sm font-medium">{q.question}</p>
                        <p className="text-[10px] text-gray-400">{q.asked_by_name} · {q.status === 'answered' ? 'Answered' : 'Waiting'}</p>
                        {q.answer && (
                          <div className="mt-2 flex items-start gap-2">
                            <p className="text-xs text-gray-600 flex-1 bg-white rounded-lg p-2">{q.answer}</p>
                            {!q.promoted_to_faq && (
                              <button onClick={async (e) => {
                                const btn = e.currentTarget;
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await fetch(`${API_URL}/api/faq/promote-from-qa/${q.id}`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                                  if (res.ok) { toast.success('Added to FAQ!'); btn.style.display = 'none'; q.promoted_to_faq = true; }
                                } catch (e2) { toast.error('Failed'); }
                              }} className="px-2 py-1 rounded-lg bg-orange-100 text-orange-700 text-[9px] font-bold hover:bg-orange-200 flex-shrink-0 whitespace-nowrap" data-testid={`promote-faq-${q.id}`}>
                                Add to FAQ
                              </button>
                            )}
                            {q.promoted_to_faq && <span className="px-2 py-1 text-[9px] font-bold text-emerald-600">Added</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              
              {/* Parent Messages Section */}
              {parentMessages.length > 0 && (
                <ParentMessagesTab parentMessages={parentMessages} formatDate={formatDate} onRefresh={fetchAllData} />
              )}
              {/* Group Messages Section */}
              <Card className="p-6 rounded-3xl">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-heading text-xl font-bold">Group Messages</h2>
                  <Button onClick={() => setShowMessageModal(true)} className="rounded-xl">
                    <Send className="w-4 h-4 mr-2" /> New Message
                  </Button>
                </div>

              <div className="space-y-4">
                {groupMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No messages yet. Send one to your group!</p>
                  </div>
                ) : (
                  groupMessages.map((msg) => (
                    <div key={msg.id} className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-purple-100 text-purple-600">
                            {msg.lead_monitor_name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{msg.lead_monitor_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(msg.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="mt-1">{msg.content}</p>
                          
                          {/* Replies */}
                          {msg.replies?.length > 0 && (
                            <div className="mt-3 space-y-2 border-l-2 border-purple-200 pl-3">
                              {msg.replies.map((reply) => (
                                <div key={reply.id} className="bg-white rounded-xl p-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xl">{reply.user_avatar}</span>
                                    <span className="font-medium text-sm">{reply.user_name}</span>
                                  </div>
                                  <p className="text-sm mt-1">{reply.content}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                            <Eye className="w-4 h-4" />
                            {msg.read_by?.length || 0} read
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
            </div>
          </TabsContent>

          {/* Work Journals Tab */}
          <TabsContent value="journal">
            <Card className="p-6 rounded-3xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-primary" />
                  Youth Work Journals
                </h2>
                <p className="text-sm text-muted-foreground">{allHighlights.length} total entries</p>
              </div>

              {allHighlights.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-xl font-semibold">No journal entries yet</p>
                  <p className="text-muted-foreground">Your youth haven&apos;t shared any work experiences yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {allHighlights.map((highlight) => {
                    const category = getCategoryInfo(highlight.category);
                    return (
                      <motion.div
                        key={highlight.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gray-50 rounded-2xl p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center text-2xl">
                            {highlight.user_avatar || '😊'}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold">{highlight.user_name}</span>
                                <Badge className={`${category.color} text-white text-xs`}>
                                  {category.icon} {category.label}
                                </Badge>
                              </div>
                              <span className="text-xs text-muted-foreground">{formatDate(highlight.created_at)}</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xl">{highlight.mood}</span>
                              <p className="text-foreground/80 flex-1">{highlight.content}</p>
                            </div>
                            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Heart className="w-4 h-4" />
                                {highlight.likes?.length || 0} likes
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const youth = assignedYouth.find(y => y.id === highlight.user_id);
                                  if (youth) viewYouthJournal(youth);
                                }}
                                className="text-xs"
                              >
                                View all from {highlight.user_name?.split(' ')[0]}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation">
            <Card className="p-6 rounded-3xl">
              <h2 className="font-heading text-xl font-bold mb-6">Content Moderation</h2>

              {moderationQueue.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-xl font-semibold text-green-600">All caught up!</p>
                  <p className="text-muted-foreground">No posts pending review</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {moderationQueue.map((post) => (
                    <div key={post.id} className="bg-gray-50 rounded-2xl p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{post.user_avatar}</span>
                          <div>
                            <p className="font-semibold">{post.user_name}</p>
                            <Badge variant="outline" className="mb-2">{post.post_type}</Badge>
                            <p className="text-muted-foreground">{post.content}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => moderatePost(post.id, 'approve')}
                            className="bg-green-500 hover:bg-green-600 rounded-xl"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => moderatePost(post.id, 'reject')}
                            className="rounded-xl"
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <h3 className="text-xl font-bold mb-4" style={{ color: colors.text }}>Send Announcement to Youth</h3>
              <p className="text-sm mb-6" style={{ color: colors.text_secondary }}>
                Create announcements to communicate important information to all youth in your program
              </p>
              <Button
                onClick={() => navigate('/admin/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                <Megaphone className="w-4 h-4 mr-2" />
                Go to Admin Dashboard
              </Button>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <ResourceManagement programId={user?.program_id} userRole="lead_monitor" />
          </TabsContent>
        </Tabs>
      </div>

      {/* New Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Send Group Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This message will be sent to all {assignedYouth.length} youth in your group.
            </p>
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message here..."
              className="min-h-[120px] rounded-xl"
            />
            <Button onClick={sendGroupMessage} className="w-full rounded-full">
              <Send className="w-4 h-4 mr-2" />
              Send to Group
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Youth Goals Modal */}
      <Dialog open={!!selectedYouth} onOpenChange={() => setSelectedYouth(null)}>
        <DialogContent className="sm:max-w-xl rounded-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center text-xl">
                {selectedYouth?.avatar_emoji || '😊'}
              </div>
              {selectedYouth?.name}&apos;s Goals
            </DialogTitle>
          </DialogHeader>
          
          {loadingGoals ? (
            <div className="text-center py-8">Loading goals...</div>
          ) : youthGoals.length === 0 ? (
            <div className="text-center py-8">
              <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No goals set yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Summer Goals */}
              <div>
                <h4 className="font-semibold text-green-600 mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" /> Summer Goals
                </h4>
                {youthGoals.filter(g => g.goal_type !== 'long_term').length === 0 ? (
                  <p className="text-sm text-muted-foreground">No summer goals</p>
                ) : (
                  <div className="space-y-2">
                    {youthGoals.filter(g => g.goal_type !== 'long_term').map((goal) => (
                      <div key={goal.id} className={`p-3 rounded-xl ${goal.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {goal.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            ) : (
                              <Target className="w-5 h-5 text-green-500" />
                            )}
                            <span className="font-medium">{goal.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {goal.status !== 'completed' && goal.deadline && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => addGoalToCalendar(goal, selectedYouth?.name)}
                                className="rounded-lg h-8 px-2"
                                title="Add to your Calendar"
                              >
                                <CalendarPlus className="w-4 h-4" />
                              </Button>
                            )}
                            <Badge variant={goal.status === 'completed' ? 'default' : 'outline'}>
                              {goal.status}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 ml-7">{goal.specific}</p>
                        {goal.deadline && (
                          <p className="text-xs text-muted-foreground mt-1 ml-7 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Due: {new Date(goal.deadline).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Long-term Goals */}
              <div>
                <h4 className="font-semibold text-indigo-600 mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" /> Long-term Goals
                </h4>
                {youthGoals.filter(g => g.goal_type === 'long_term').length === 0 ? (
                  <p className="text-sm text-muted-foreground">No long-term goals</p>
                ) : (
                  <div className="space-y-2">
                    {youthGoals.filter(g => g.goal_type === 'long_term').map((goal) => (
                      <div key={goal.id} className={`p-3 rounded-xl ${goal.status === 'completed' ? 'bg-indigo-50 border border-indigo-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {goal.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-indigo-500" />
                            ) : (
                              <Target className="w-5 h-5 text-indigo-500" />
                            )}
                            <span className="font-medium">{goal.title}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {goal.status !== 'completed' && goal.deadline && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => addGoalToCalendar(goal, selectedYouth?.name)}
                                className="rounded-lg h-8 px-2"
                                title="Add to your Calendar"
                              >
                                <CalendarPlus className="w-4 h-4" />
                              </Button>
                            )}
                            <Badge variant={goal.status === 'completed' ? 'default' : 'outline'}>
                              {goal.status}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 ml-7">{goal.specific}</p>
                        {goal.deadline && (
                          <p className="text-xs text-muted-foreground mt-1 ml-7 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Due: {new Date(goal.deadline).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Youth Journal Modal */}
      <Dialog open={!!selectedYouthJournal} onOpenChange={() => setSelectedYouthJournal(null)}>
        <DialogContent className="sm:max-w-xl rounded-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center text-xl">
                {selectedYouthJournal?.avatar_emoji || '😊'}
              </div>
              {selectedYouthJournal?.name}&apos;s Work Journal
            </DialogTitle>
          </DialogHeader>
          
          {youthHighlights.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No journal entries yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{youthHighlights.length} entries in their work journal</p>
              {youthHighlights.map((highlight) => {
                const category = getCategoryInfo(highlight.category);
                return (
                  <div key={highlight.id} className="bg-gray-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${category.color} text-white`}>
                        {category.icon} {category.label}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDate(highlight.created_at)}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-xl">{highlight.mood}</span>
                      <p className="text-foreground/80 flex-1">{highlight.content}</p>
                    </div>
                    <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs text-muted-foreground">
                      <Heart className="w-3 h-3" />
                      {highlight.likes?.length || 0} likes
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadMonitorDashboard;
